import React, { createContext, useEffect, useState, useContext } from "react";
import { useApolloClient, useMutation } from "react-apollo";
import PreviewModeMutation from "./PreviewModeMutation.gql";
import XRayModeMutation from "./XRayModeMutation.gql";
import XRayModeQuery from "./XRayModeQuery.gql";
import PropTypes from "prop-types";

export const AUTO_REFRESH_INTERVAL_SECONDS = 3;
const AUTO_REFRESH_INTERVAL_IN_MS = AUTO_REFRESH_INTERVAL_SECONDS * 1000;

const EditorialToolboxContext = createContext();
export const useEditorialToolbox = () => {
  const context = useContext(EditorialToolboxContext);
  if (context === undefined) {
    throw new Error(
      "useEditorialToolbox must be used within a EditorialToolboxProvider"
    );
  }

  return context;
};

const usePreviewMode = (initialPreviewModeState) => {
  const [updatePreviewMode] = useMutation(PreviewModeMutation);
  const [isPreviewMode, setPreviewModeState] = useState(
    initialPreviewModeState
  );

  const setPreviewMode = (input) => {
    updatePreviewMode({
      variables: {
        input,
      },
    }).then((res) => {
      if (res.data.contribution?.setPreviewMode?.success) {
        setPreviewModeState(res.data.contribution.setPreviewMode.enabled);
      }
    });
  };

  return [isPreviewMode, setPreviewMode];
};

const useXRayMode = (initialXRayModeState) => {
  const apolloClient = useApolloClient();
  const [updateXRayMode] = useMutation(XRayModeMutation);
  const [isXRayMode, setXRayModeState] = useState(initialXRayModeState);

  const setXRayMode = (input) => {
    setXRayModeState(input);

    updateXRayMode({
      variables: {
        input,
      },
    }).then((res) => {
      if (res.data.contribution?.setXRayMode?.success) {
        // Settings states are retrieved from feature flags and
        // those are not updated when contribution mode mutations are made
        // TODO: Use a root-based provider instead after
        // refactoring the magic-button module into the core
        // TODO(alt): use directly a `contribution` query in toolbox instead ?
        apolloClient.writeQuery({
          query: XRayModeQuery,
          data: {
            isFeatureActive: res.data.contribution.setXRayMode.enabled,
          },
        });
        setXRayModeState(res.data.contribution.setXRayMode.enabled);
      } else {
        setXRayModeState(!input);
      }
    });
  };

  return [isXRayMode, setXRayMode];
};

/** @type {React.FunctionComponent<PropTypes.InferProps<typeof propTypes>>} */
const EditorialToolboxProvider = ({
  children,
  isPreviewModeEnabled,
  isXRayModeEnabled,
}) => {
  const apolloClient = useApolloClient();
  const [isAutoRefreshing, setAutoRefreshing] = useState(false);
  const [isPreviewMode, setPreviewMode] = usePreviewMode(isPreviewModeEnabled);
  const [isXRayMode, setXRayMode] = useXRayMode(isXRayModeEnabled);

  useEffect(() => {
    if (isPreviewMode) {
      setAutoRefreshing(isPreviewMode);
    }
  }, [isPreviewMode]);

  useEffect(() => {
    if (!isAutoRefreshing) {
      return;
    }

    let timeout;
    const resetStoreTimeout = () => {
      timeout = setTimeout(() => {
        apolloClient.resetStore().then(resetStoreTimeout);
      }, AUTO_REFRESH_INTERVAL_IN_MS);
    };

    resetStoreTimeout();

    return () => {
      clearTimeout(timeout);
    };
  }, [isAutoRefreshing, apolloClient]);

  return (
    <EditorialToolboxContext.Provider
      value={{
        isAutoRefreshing,
        setAutoRefreshing,
        isPreviewMode,
        setPreviewMode,
        isXRayMode,
        setXRayMode,
      }}
    >
      {children}
    </EditorialToolboxContext.Provider>
  );
};

const propTypes = {
  isPreviewModeEnabled: PropTypes.bool.isRequired,
  isXRayModeEnabled: PropTypes.bool.isRequired,
};
EditorialToolboxProvider.propTypes = propTypes;
export default EditorialToolboxProvider;
